import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, finalize, share } from 'rxjs';
import { environment } from '../../environments/environment';
import { PUBLIC_API_REQUEST } from './public-api-request.context';

/**
 * Shares identical GET requests to the REST API that are in flight at the same
 * time. A page load fires the same calls from several components at once
 * (e.g. `/v1/profile` four times), and the single-CPU backend answers them one
 * after another. Nothing is cached: once the response has arrived, the next
 * call goes to the server again.
 */
@Injectable()
export class InflightGetDedupeInterceptor implements HttpInterceptor {
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly inflight = new Map<string, Observable<HttpEvent<unknown>>>();

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (
      req.method !== 'GET' ||
      req.reportProgress ||
      !req.url.startsWith(this.apiBaseUrl)
    ) {
      return next.handle(req);
    }

    const key = this.keyFor(req);
    const shared = this.inflight.get(key);
    if (shared) {
      return shared;
    }

    const request$ = next.handle(req).pipe(
      finalize(() => this.inflight.delete(key)),
      share()
    );
    this.inflight.set(key, request$);
    return request$;
  }

  private keyFor(req: HttpRequest<unknown>): string {
    const headers = req.headers
      .keys()
      .sort()
      .map(name => `${name}=${req.headers.getAll(name)?.join(',')}`)
      .join('&');
    return [
      req.urlWithParams,
      req.responseType,
      req.withCredentials,
      req.context.get(PUBLIC_API_REQUEST),
      headers,
    ].join('|');
  }
}
